import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CursorTrail = () => {
  const [particles, setParticles] = useState([]);
  const [isEnabled, setIsEnabled] = useState(true);
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isGameActive, setIsGameActive] = useState(false);

  // Detect game-active state on body (set by TheDescent during gameplay)
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsGameActive(document.body.classList.contains('game-active'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Particle colors matching the gremlin theme
  const colors = [
    'rgba(185, 21, 204, 0.8)',  // Primary purple
    'rgba(44, 255, 5, 0.8)',    // Secondary green
    'rgba(0, 200, 255, 0.8)',   // Accent cyan
    'rgba(230, 60, 180, 0.8)',  // Chaos pink
  ];

  const createParticle = useCallback((x, y) => {
    const particle = {
      id: Date.now() + Math.random(),
      x,
      y,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: isMobile ? 4 + Math.random() * 4 : 6 + Math.random() * 8, // Smaller on mobile
      vx: (Math.random() - 0.5) * 3,
      vy: (Math.random() - 0.5) * 3,
    };
    return particle;
  }, [isMobile]);

  // Update cursor position and create particles
  useEffect(() => {
    let lastX = 0;
    let lastY = 0;
    let frameCount = 0;

    const handleMouseMove = (e) => {
      // Always update cursor position and make visible
      setCursorPos({ x: e.clientX, y: e.clientY });
      setIsVisible(true);

      if (!isEnabled) return;

      frameCount++;
      // Create particles less frequently on mobile for performance
      const frameSkip = isMobile ? 5 : 3;
      if (frameCount % frameSkip !== 0) return;

      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Only create particles when moving fast enough
      if (distance > (isMobile ? 10 : 5)) {
        const newParticle = createParticle(e.clientX, e.clientY);
        const maxParticles = isMobile ? 10 : 20; // Fewer particles on mobile
        setParticles(prev => [...prev.slice(-maxParticles), newParticle]);
        lastX = e.clientX;
        lastY = e.clientY;
      }
    };

    // Use touch events on mobile
    const handleTouchMove = (e) => {
      if (!isEnabled || !e.touches[0]) return;
      const touch = e.touches[0];
      
      frameCount++;
      if (frameCount % 5 !== 0) return;

      const dx = touch.clientX - lastX;
      const dy = touch.clientY - lastY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > 10) {
        const newParticle = createParticle(touch.clientX, touch.clientY);
        setParticles(prev => [...prev.slice(-10), newParticle]);
        lastX = touch.clientX;
        lastY = touch.clientY;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    if (isMobile) {
      window.addEventListener('touchmove', handleTouchMove, { passive: true });
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isEnabled, createParticle, isMobile]);

  // Clean up old particles - faster cleanup on mobile
  useEffect(() => {
    const cleanupInterval = isMobile ? 150 : 100;
    const particleLifetime = isMobile ? 600 : 1000;
    const cleanup = setInterval(() => {
      setParticles(prev => prev.filter(p => Date.now() - p.id < particleLifetime));
    }, cleanupInterval);
    return () => clearInterval(cleanup);
  }, [isMobile]);

  // Add/remove custom cursor style - only on desktop, disabled during active games
  useEffect(() => {
    if (isEnabled && !isMobile) {
      // Hide cursor on body and all interactive elements, but NOT during active games
      const style = document.createElement('style');
      style.id = 'custom-cursor-style';
      style.textContent = `
        body:not(.game-active) *, body:not(.game-active) *::before, body:not(.game-active) *::after {
          cursor: none !important;
        }
      `;
      document.head.appendChild(style);
    } else {
      // Remove custom style
      const style = document.getElementById('custom-cursor-style');
      if (style) {
        style.remove();
      }
    }
    return () => {
      const style = document.getElementById('custom-cursor-style');
      if (style) {
        style.remove();
      }
    };
  }, [isEnabled, isMobile]);

  return (
    <>
      {/* Custom Particle Cursor - Only on desktop, hidden during games */}
      {isEnabled && isVisible && !isMobile && !isGameActive && (
        <div
          className="fixed pointer-events-none z-[9999]"
          style={{
            left: cursorPos.x - 10,
            top: cursorPos.y - 10,
            width: 20,
            height: 20,
          }}
        >
          {/* Single purple glowing circle */}
          <motion.div
            className="rounded-full"
            style={{
              width: 20,
              height: 20,
              background: '#b915cc',
              boxShadow: '0 0 10px #b915cc, 0 0 20px #b915cc, 0 0 30px rgba(185, 21, 204, 0.6)',
            }}
            animate={{
              scale: [1, 1.15, 1],
              boxShadow: [
                '0 0 10px #b915cc, 0 0 20px #b915cc, 0 0 30px rgba(185, 21, 204, 0.6)',
                '0 0 15px #b915cc, 0 0 25px #b915cc, 0 0 40px rgba(185, 21, 204, 0.8)',
                '0 0 10px #b915cc, 0 0 20px #b915cc, 0 0 30px rgba(185, 21, 204, 0.6)',
              ],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
      )}

      {/* Cursor Trail Particles */}
      <div className="fixed inset-0 pointer-events-none z-[9998]">
        <AnimatePresence>
          {particles.map(particle => (
            <motion.div
              key={particle.id}
              initial={{
                x: particle.x,
                y: particle.y,
                scale: 1,
                opacity: 1,
              }}
              animate={{
                x: particle.x + particle.vx * (isMobile ? 20 : 30),
                y: particle.y + particle.vy * (isMobile ? 20 : 30),
                scale: 0,
                opacity: 0,
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: isMobile ? 0.5 : 0.8, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                width: particle.size,
                height: particle.size,
                borderRadius: '50%',
                backgroundColor: particle.color,
                boxShadow: isMobile ? 'none' : `0 0 ${particle.size * 2}px ${particle.color}`,
                transform: 'translate(-50%, -50%)',
              }}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Toggle Button - Hidden on mobile and during games */}
      {!isMobile && !isGameActive && (
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1 }}
          onClick={() => setIsEnabled(!isEnabled)}
          style={{ cursor: 'pointer' }}
        className={`fixed bottom-4 left-4 z-[9999] p-3 rounded-full border-2 transition-all duration-300 ${
          isEnabled
            ? 'bg-primary/20 border-primary text-primary shadow-[0_0_15px_rgba(185,21,204,0.5)]'
            : 'bg-card/50 border-border text-muted-foreground'
        }`}
        title={isEnabled ? 'Disable custom cursor' : 'Enable custom cursor'}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
          />
        </svg>
      </motion.button>
      )}
    </>
  );
};

export default CursorTrail;
